import './App.css';
import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import JobsPage from './components/Job/JobsPage';
import TeliviIT from './components/TeliviIt';
import PlatformsPage from './components/PlatformsPage';
import PricingPage from './components/PricingPage';
import HowItWorksPage from './components/HowItWorksPage';
import SecurityPage from './components/SecurityPage';
import ContactPage from './components/ContactPage';
import PaymentsDisclosurePage from './components/PaymentsDisclosurePage';
import ProtectedRoute from './components/Authentication/ProtectedRoute';
import AddJobPage from './components/Job/AddJobPage';
import AuthProviderSwitch, {
  AUTH_PROVIDERS,
  getAuthProvider,
  useAuthProvider
} from './components/Authentication/AuthProviderSwitch';
import Signup from './components/Authentication/Signup';
import LoginPage from './components/Authentication/LoginPage';
import CallbackPage from './components/Authentication/CallBackPage';
import JobDashboard from './components/Job/JobDashboard';
import Logout from './components/Authentication/Logout';
import VerifyEmail from './components/Authentication/VerifyEmail';
import ApplicationForm from './components/Application/ApplicationForm';
import DashboardForm from './components/Admin/DashboardForm';
import PaymentsDashboardPage from './components/payments/PaymentsDashboardPage';
import SubscribePage from './components/Subscribe/SubscribePage';
import LearnReact from './components/Learning/LearnReact';
import TestReact from './components/Learning/TestReact';
import ReactCourse from './components/Learning/ReactCourse';
import InternTest from './components/Learning/InternTest';
import InternLearn from './components/Learning/InternLearnChaptered';
import LearnJava from './components/Learning/LearnJava';
import LearningDashboard from './components/Learning/LearningDashboard';
import ClerkLoginPage from './components/ClerkAuthentication/ClerkLoginPage';
import ClerkSignUpPage from './components/ClerkAuthentication/ClerkSignUpPage';
import ClerkLogoutPage from './components/ClerkAuthentication/ClerkLogoutPage';
import AutoActivateOrg from './components/ClerkAuthentication/AutoActivateOrg';
import ClientProtectedRoute from './components/ClientFlow/ClientProtectedRoute';
import ClientClerkLoginPage from './components/ClientFlow/ClientClerkLoginPage';
import ClientClerkSignUpPage from './components/ClientFlow/ClientClerkSignUpPage';
import ClientPortalLayout from './components/ClientFlow/ClientPortalLayout';
import ClientCompanyUsersPage from './components/ClientFlow/ClientCompanyUsersPage';
import ClientSubscriptionsPage from './components/ClientFlow/ClientSubscriptionsPage';
import ClientPurchasesPage from './components/ClientFlow/ClientPurchasesPage';
import ClientIntegrationSettingsPage from './components/ClientFlow/ClientIntegrationSettingsPage';
import ClientSecurityPage from './components/ClientFlow/ClientSecurityPage';
import ClientHelpdeskPage from './components/ClientFlow/ClientHelpdeskPage';
import { hasClientAccess } from './components/Authentication/roleAccess';

const HomeRoute = ({ authProvider }) => {
  const { isAuthenticated, isLoading, orgRole, has } = useAuthProvider();

  if (authProvider === AUTH_PROVIDERS.CLERK) {
    if (isLoading) {
      return (
        <Layout>
          <TeliviIT />
        </Layout>
      );
    }

    if (isAuthenticated) {
      if (hasClientAccess({ orgRole, has })) {
        return <Navigate to="/client/account" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <Layout>
      <TeliviIT />
    </Layout>
  );
};

function App() {
  const authProvider = getAuthProvider();

  return (
    <ChakraProvider>
      <Router>
        <AuthProviderSwitch>
          {authProvider === AUTH_PROVIDERS.CLERK ? <AutoActivateOrg /> : null}
          <Routes>
            <Route path="/" element={<HomeRoute authProvider={authProvider} />} />
            <Route
              path="/subscribe"
              element={
                <Layout>
                  <SubscribePage />
                </Layout>
              }
            />
            <Route
              path="/platforms"
              element={
                <Layout>
                  <PlatformsPage />
                </Layout>
              }
            />
            <Route
              path="/pricing"
              element={
                <Layout>
                  <PricingPage />
                </Layout>
              }
            />
            <Route
              path="/how-it-works"
              element={
                <Layout>
                  <HowItWorksPage />
                </Layout>
              }
            />
            <Route
              path="/security"
              element={
                <Layout>
                  <SecurityPage />
                </Layout>
              }
            />
            <Route
              path="/contact"
              element={
                <Layout>
                  <ContactPage />
                </Layout>
              }
            />
            <Route
              path="/payments-disclosure"
              element={
                <Layout>
                  <PaymentsDisclosurePage />
                </Layout>
              }
            />
            <Route
              path="/login/*"
              element={
                <Layout>
                  {authProvider === AUTH_PROVIDERS.CLERK ? <ClerkLoginPage /> : <LoginPage />}
                </Layout>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                authProvider === AUTH_PROVIDERS.CLERK ? (
                  <Layout>
                    <ClerkSignUpPage />
                  </Layout>
                ) : (
                  <Navigate to="/signup" />
                )
              }
            />
            <Route
              path="/signup"
              element={
                <Layout>
                  {authProvider === AUTH_PROVIDERS.AUTH0 ? <Signup /> : <Navigate to="/sign-up" />}
                </Layout>
              }
            />
            <Route
              path="/callback"
              element={
                authProvider === AUTH_PROVIDERS.AUTH0 ? (
                  <Layout>
                    <CallbackPage />
                  </Layout>
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/verify-email"
              element={authProvider === AUTH_PROVIDERS.AUTH0 ? <VerifyEmail /> : <Navigate to="/" />}
            />
            <Route
              path="/logout"
              element={authProvider === AUTH_PROVIDERS.CLERK ? <ClerkLogoutPage /> : <Logout />}
            />

            <Route
              path="/client/login/*"
              element={
                <Layout>
                  {authProvider === AUTH_PROVIDERS.CLERK ? <ClientClerkLoginPage /> : <Navigate to="/login" replace />}
                </Layout>
              }
            />
            <Route
              path="/client/sign-up/*"
              element={
                <Layout>
                  {authProvider === AUTH_PROVIDERS.CLERK ? <ClientClerkSignUpPage /> : <Navigate to="/login" replace />}
                </Layout>
              }
            />
            <Route path="/client/signup" element={<Navigate to="/client/sign-up" replace />} />
            <Route path="/client/callback" element={<Navigate to="/client/account" replace />} />

            <Route
              path="/client"
              element={
                <Layout>
                  {authProvider === AUTH_PROVIDERS.CLERK ? (
                    <ClientProtectedRoute>
                      <ClientPortalLayout />
                    </ClientProtectedRoute>
                  ) : (
                    <Navigate to="/login" replace />
                  )}
                </Layout>
              }
            >
              <Route index element={<Navigate to="account" replace />} />
              <Route path="portal" element={<Navigate to="/client/account" replace />} />
              <Route path="companies" element={<Navigate to="/client/account" replace />} />
              <Route path="account" element={<ClientCompanyUsersPage />} />
              <Route path="subscriptions" element={<ClientSubscriptionsPage />} />
              <Route path="purchases" element={<ClientPurchasesPage />} />
              <Route path="integrations" element={<ClientIntegrationSettingsPage />} />
              <Route path="security" element={<ClientSecurityPage />} />
              <Route path="helpdesk" element={<ClientHelpdeskPage />} />
            </Route>

            <Route path="/jobs" element={<Layout><JobsPage /></Layout>} />
            <Route path="/react-course" element={<Layout><ReactCourse /></Layout>} />
            <Route path="/learning-react" element={<Layout><LearnReact /></Layout>} />
            <Route path="/learning-java" element={<Layout><LearnJava /></Layout>} />
            <Route path="/intern-learn" element={<Layout><InternLearn /></Layout>} />
            <Route path="/test-react" element={<Layout><TestReact /></Layout>} />
            <Route path="/intern-test" element={<Layout><InternTest /></Layout>} />
            <Route path="/add" element={<Layout><ProtectedRoute><AddJobPage /></ProtectedRoute></Layout>} />
            <Route path="/apply/:jobId" element={<Layout><ApplicationForm /></Layout>} />
            <Route path="/payments/dashboard" element={<Navigate to="/payments/overview" />} />
            <Route path="/payments" element={<Navigate to="/payments/overview" />} />
            <Route path="/payments/*" element={<Layout><ProtectedRoute><PaymentsDashboardPage /></ProtectedRoute></Layout>} />
            <Route
              path="/dashboard"
              element={
                <Layout>
                  <ProtectedRoute>
                    <DashboardForm />
                  </ProtectedRoute>
                </Layout>
              }
            />
            <Route
              path="/dashboard/learning"
              element={
                <Layout>
                  <ProtectedRoute>
                    <LearningDashboard />
                  </ProtectedRoute>
                </Layout>
              }
            />
            <Route
              path="/jobs/dashboard"
              element={
                <Layout>
                  <ProtectedRoute>
                    <JobDashboard />
                  </ProtectedRoute>
                </Layout>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AuthProviderSwitch>
      </Router>
    </ChakraProvider>
  );
}

export default App;
